import React, { useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { VesselCallOutletContext } from "@features/vessel-call/create/CreateVesselCallContainer";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import type { VesselCallFormData } from "@features/vessel-call/create/schema/vesselCallFormSchema";
import { SasTokenPrefix } from "@services/user/types";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useVesselCallFormStore } from "../../store/useVesselCallForm.store";
import PortCalls from "./PortCalls";

const LANG_BASE = "vessel-call.create";
const TEST_ID_BASE = "solas-chapter-isps-code";

const SolasChapterIspsCode: React.FC = () => {
  const { formData, updateFormData, watch } = useOutletContext<VesselCallOutletContext>();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const documentConditions = { ...FILE_UPLOAD_PROFILES.certificates, hideErrors: false };

  const isscIsValid = watch(FORM_FIELDS.ISSC_IS_VALID);
  const hasSpecialMeasures = watch(FORM_FIELDS.SPECIAL_SECURITY_MEASURES);
  const hasSecurityGuards = watch(FORM_FIELDS.SECURITY_GUARDS_ON_BOARD);
  const hasArmsAndAmmunition = watch(FORM_FIELDS.ARM_AMMUNITION_ON_BOARD);
  const vesselHasDeficiencies = watch(FORM_FIELDS.VESSEL_HAS_DEFICIENCIES);

  const formStore = useVesselCallFormStore;

  const flagStatePermissionDocumentUrl = useDocumentUpload(
    FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  const armAmmunitionProofDocumentUrl = useDocumentUpload(
    FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  const deficienciesDocumentsUrl = useDocumentUpload(
    FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL,
    SasTokenPrefix.VESSEL_CALL,
    formData,
    formStore,
  );

  const radioOptions = useMemo(
    () => [
      { label: t("common.yes"), value: true },
      { label: t("common.no"), value: false },
    ],
    [t],
  );

  const securityLevelOptions = useMemo(
    () => [
      { label: "1", value: "1" },
      { label: "2", value: "2" },
      { label: "3", value: "3" },
    ],
    [t],
  );

  const resetField = <K extends keyof VesselCallFormData>(
    field: K,
    value: VesselCallFormData[K],
  ) => {
    formData.setValue(field, value, {
      shouldDirty: true,
      shouldValidate: false,
    });
    updateFormData(field, value);
    formData.clearErrors(field);
  };

  const clearIsscSection = () => {
    resetField(FORM_FIELDS.ISSC_NUMBER, "");
    resetField(FORM_FIELDS.ISSC_ISSUE_DATE, "");
    resetField(FORM_FIELDS.ISSC_EXPIRY_DATE, "");
    resetField(FORM_FIELDS.ISSC_ISSUED_BY, "");
  };

  const clearSpecialSecurityMeasures = () => {
    resetField(FORM_FIELDS.TAKEN_MEASURES, "");
  };

  const clearSecurityGuardsSection = () => {
    resetField(FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS, "");
    flagStatePermissionDocumentUrl.handleRemove();
    formData.clearErrors(FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL);
  };

  const clearArmsAndAmmunitionSection = () => {
    armAmmunitionProofDocumentUrl.handleRemove();
    formData.clearErrors(FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL);
  };

  const clearDeficienciesSection = () => {
    resetField(FORM_FIELDS.DEFICIENCIES, "");
    deficienciesDocumentsUrl.handleRemove();
    formData.clearErrors(FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL);
  };

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.form-sections.solas-chapter`)}>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.SECURITY_LEVEL>
          name={FORM_FIELDS.SECURITY_LEVEL}
          type="autocomplete"
          options={securityLevelOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.SECURITY_LEVEL}`)}
          testId={`${TEST_ID_BASE}-security-level`}
          onFieldChange={updateFormData}
        />
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.ISSC_IS_VALID>
            name={FORM_FIELDS.ISSC_IS_VALID}
            type="radio"
            options={radioOptions}
            horizontalRadioButtons
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ISSC_IS_VALID}`)}
            testId={`${TEST_ID_BASE}-issc-is-valid`}
            onFieldChange={(key, value) => {
              if (!value) {
                clearIsscSection();
              }
              updateFormData(key, value);
            }}
          />
        </div>
        {isscIsValid ? (
          <div className="grid grid-cols-3 gap-4 col-span-full">
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.ISSC_NUMBER>
              name={FORM_FIELDS.ISSC_NUMBER}
              type="text"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ISSC_NUMBER}`)}
              testId={`${TEST_ID_BASE}-issc-number`}
              onFieldChange={updateFormData}
              maxChars={30}
            />
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.ISSC_ISSUE_DATE>
              name={FORM_FIELDS.ISSC_ISSUE_DATE}
              type="date-picker"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ISSC_ISSUE_DATE}`)}
              testId={`${TEST_ID_BASE}-issc-issue-date`}
              onFieldChange={updateFormData}
              timePicker={false}
              dateConstraint="not-future"
            />
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.ISSC_EXPIRY_DATE>
              name={FORM_FIELDS.ISSC_EXPIRY_DATE}
              type="date-picker"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ISSC_EXPIRY_DATE}`)}
              testId={`${TEST_ID_BASE}-issc-expiry-date`}
              onFieldChange={updateFormData}
              timePicker={false}
              dateConstraint="not-past"
            />
            <div className="col-span-full">
              <FormItem<VesselCallFormData, typeof FORM_FIELDS.ISSC_ISSUED_BY>
                name={FORM_FIELDS.ISSC_ISSUED_BY}
                type="text"
                label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ISSC_ISSUED_BY}`)}
                testId={`${TEST_ID_BASE}-issc-issued-by`}
                onFieldChange={updateFormData}
                maxChars={100}
              />
            </div>
          </div>
        ) : null}
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.APPROVED_SSP_ONBOARD>
          name={FORM_FIELDS.APPROVED_SSP_ONBOARD}
          type="radio"
          options={radioOptions}
          horizontalRadioButtons
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.APPROVED_SSP_ONBOARD}`)}
          testId={`${TEST_ID_BASE}-approved-ssp-on-board`}
          onFieldChange={updateFormData}
        />
        <div className="col-span-full">
          <PortCalls />
        </div>
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.SPECIAL_SECURITY_MEASURES>
            name={FORM_FIELDS.SPECIAL_SECURITY_MEASURES}
            type="radio"
            options={radioOptions}
            horizontalRadioButtons
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.SPECIAL_SECURITY_MEASURES}`)}
            testId={`${TEST_ID_BASE}-special-security-measures`}
            onFieldChange={(key, value) => {
              if (!value) {
                clearSpecialSecurityMeasures();
              }
              updateFormData(key, value);
            }}
          />
        </div>
        {hasSpecialMeasures ? (
          <div className="col-span-full">
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.TAKEN_MEASURES>
              name={FORM_FIELDS.TAKEN_MEASURES}
              type="textarea"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.TAKEN_MEASURES}`)}
              testId={`${TEST_ID_BASE}-taken-measures`}
              onFieldChange={updateFormData}
              maxChars={300}
              minRows={1}
            />
          </div>
        ) : null}
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.SECURITY_GUARDS_ON_BOARD>
            name={FORM_FIELDS.SECURITY_GUARDS_ON_BOARD}
            type="radio"
            options={radioOptions}
            horizontalRadioButtons
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.SECURITY_GUARDS_ON_BOARD}`)}
            testId={`${TEST_ID_BASE}-security-guards-on-board`}
            onFieldChange={(key, value) => {
              if (!value) {
                clearSecurityGuardsSection();
              }
              updateFormData(key, value);
            }}
          />
        </div>
        {hasSecurityGuards ? (
          <>
            <div className="col-span-full grid grid-cols-3 gap-4">
              <FormItem<VesselCallFormData, typeof FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS>
                name={FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS}
                type="number"
                label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS}`)}
                testId={`${TEST_ID_BASE}-number-of-security-guards`}
                onFieldChange={updateFormData}
                maxDigits={5}
                canBeginWithZero={false}
              />
            </div>
            <div className="col-span-full">
              <FormSection title={t(`${LANG_BASE}.form-fields.flagStatePermissionDocumentUrl`)}>
                <div className="col-span-full">
                  <FormItem<
                    VesselCallFormData,
                    typeof FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL
                  >
                    name={FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL}
                    type="file"
                    testId={`${TEST_ID_BASE}-flag-state-permission`}
                    uploadConditions={documentConditions}
                    files={flagStatePermissionDocumentUrl.uploadEntries}
                    setFiles={flagStatePermissionDocumentUrl.setUploadEntries}
                    onUpload={(_, files) => flagStatePermissionDocumentUrl.handleUpload(files)}
                  />
                  {flagStatePermissionDocumentUrl.uploadEntries.length > 0 && (
                    <div className="flex-1">
                      <SingleFile
                        testId={`${TEST_ID_BASE}-document-flag-state-permission`}
                        file={flagStatePermissionDocumentUrl.uploadEntries[0]}
                        fieldName={FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL}
                        setFiles={() => flagStatePermissionDocumentUrl.setUploadEntries([])}
                        removeFn={flagStatePermissionDocumentUrl.handleRemove}
                      />
                    </div>
                  )}
                </div>
              </FormSection>
            </div>
          </>
        ) : null}
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.ARM_AMMUNITION_ON_BOARD>
            name={FORM_FIELDS.ARM_AMMUNITION_ON_BOARD}
            type="radio"
            options={radioOptions}
            horizontalRadioButtons
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ARM_AMMUNITION_ON_BOARD}`)}
            testId={`${TEST_ID_BASE}-arm-ammunition-on-board`}
            onFieldChange={(key, value) => {
              if (!value) {
                clearArmsAndAmmunitionSection();
              }
              updateFormData(key, value);
            }}
          />
        </div>
        {hasArmsAndAmmunition ? (
          <div className="col-span-full">
            <FormSection title={t(`${LANG_BASE}.form-fields.armAmmunitionProofDocumentUrl`)}>
              <div className="col-span-full">
                <FormItem<VesselCallFormData, typeof FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL>
                  name={FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL}
                  type="file"
                  uploadConditions={documentConditions}
                  testId={`${TEST_ID_BASE}-arm-ammunition-proof`}
                  files={armAmmunitionProofDocumentUrl.uploadEntries}
                  setFiles={armAmmunitionProofDocumentUrl.setUploadEntries}
                  onUpload={(_, files) => armAmmunitionProofDocumentUrl.handleUpload(files)}
                  notSuccess={armAmmunitionProofDocumentUrl.notSuccess}
                />
                {armAmmunitionProofDocumentUrl.uploadEntries.length > 0 && (
                  <div className="flex-1">
                    <SingleFile
                      testId={`${TEST_ID_BASE}-document-arm-ammunition-proof`}
                      file={armAmmunitionProofDocumentUrl.uploadEntries[0]}
                      fieldName={FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL}
                      setFiles={() => armAmmunitionProofDocumentUrl.setUploadEntries([])}
                      removeFn={armAmmunitionProofDocumentUrl.handleRemove}
                    />
                  </div>
                )}
              </div>
            </FormSection>
          </div>
        ) : null}
        <div className="">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.VESSEL_HAS_DEFICIENCIES>
            name={FORM_FIELDS.VESSEL_HAS_DEFICIENCIES}
            type="radio"
            options={radioOptions}
            horizontalRadioButtons
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.VESSEL_HAS_DEFICIENCIES}`)}
            testId={`${TEST_ID_BASE}-vessel-has-deficiencies`}
            onFieldChange={(key, value) => {
              if (!value) {
                clearDeficienciesSection();
              }
              updateFormData(key, value);
            }}
          />
        </div>
        {vesselHasDeficiencies ? (
          <div className="col-span-full">
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.DEFICIENCIES>
              name={FORM_FIELDS.DEFICIENCIES}
              type="text"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.DEFICIENCIES}`)}
              testId={`${TEST_ID_BASE}-deficiencies`}
              onFieldChange={updateFormData}
              maxChars={300}
            />
          </div>
        ) : null}
        {vesselHasDeficiencies ? (
          <div className="col-span-full">
            <FormSection title={t(`${LANG_BASE}.form-fields.deficienciesDocumentsUrl`)}>
              <div className="col-span-full">
                <FormItem<VesselCallFormData, typeof FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL>
                  name={FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL}
                  type="file"
                  uploadConditions={documentConditions}
                  testId={`${TEST_ID_BASE}-deficiencies-documents`}
                  files={deficienciesDocumentsUrl.uploadEntries}
                  setFiles={deficienciesDocumentsUrl.setUploadEntries}
                  onUpload={(_, files) => deficienciesDocumentsUrl.handleUpload(files)}
                  notSuccess={deficienciesDocumentsUrl.notSuccess}
                />
                {deficienciesDocumentsUrl.uploadEntries.length > 0 && (
                  <div className="flex-1">
                    <SingleFile
                      testId={`${TEST_ID_BASE}-uploaded-deficiencies-documents`}
                      file={deficienciesDocumentsUrl.uploadEntries[0]}
                      fieldName={FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL}
                      setFiles={() => deficienciesDocumentsUrl.setUploadEntries([])}
                      removeFn={deficienciesDocumentsUrl.handleRemove}
                    />
                  </div>
                )}
              </div>
            </FormSection>
          </div>
        ) : null}
      </FormSection>
    </FormProvider>
  );
};

export default SolasChapterIspsCode;
